import { Button, Card, CardContent, Grid } from "@mui/material";
import SettingsReport from "./SettingsReport";
import {
  PerformanceReportTypeEnum,
  PDFReportRequest,
  PDFReportClientInfoItem,
  PDFReportThemeInfoItem,
  PDFReportValueItem,
  PDFReportTableColumnRow,
  ReportPdfResponse,
  OrionTransaction,
} from "helpers/types";
import {
  getLastPerformanceStockClientId,
  getLastPerformanceStockAccountIds,
} from "helpers";
import ReportQuarterTable, { defaultQuarterRows } from "./QuarterTableReport";
import SingleValueCard from "./SingleValueCard";
import MultiValuesCard from "./MultiValuesCard";
import {
  SingleValueCardColor,
  SingleValueCardType,
  ReportQuarterTableColumn,
  type QuarterRow,
} from "constants/index";
import { ReportValueCardProps } from "helpers/types";
import { useStockReportValue } from "hooks/useReportStorage";
import { OverlayVsStockChart } from "./OverlayVsStockChart";
import { useMemo, useState, useCallback } from "react";
import {
  useGetNewReportMutation,
  useGetReportPdfMutation,
} from "features/trading/accounts";
import {
  ReportSettingsProvider,
  useReportSettings,
} from "./ReportSettingsContext";
import TransactionModal from "./TransactionModal";
import RequestNotification from "components/ui/RequestNotification";

function StockReportContent() {
  // SingleValueCard hooks
  const [startingValue, setStartingValue] = useStockReportValue(
    "starting_value_stock",
    3000000,
  );
  const [currentValue, setCurrentValue] = useStockReportValue(
    "current_value_stock",
    3600000,
  );
  const [cumulativeCallIncome, setCumulativeCallIncome] = useStockReportValue(
    "cumulative_call_income_stock",
    210000,
  );
  const [netRealizedTLHLosses, setNetRealizedTLHLosses] = useStockReportValue(
    "net_realized_tlh_losses_stock",
    180000,
  );
  const [overlayVsStockAlone, setOverlayVsStockAlone] = useStockReportValue(
    "overlay_vs_stock_alone_stock",
    1.2,
  );
  const [overlayReturnTWR, setOverlayReturnTWR] = useStockReportValue(
    "overlay_return_twr_stock",
    7.1,
  );

  const overlayValue = useMemo(
    () => (currentValue as number) + (cumulativeCallIncome as number),
    [currentValue, cumulativeCallIncome],
  );

  const [tableRows, setTableRows] = useState<QuarterRow[]>(defaultQuarterRows);
  const [overlayChartLimit, setOverlayChartLimit] = useState(4_000_000);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [calculationError, setCalculationError] = useState("");
  const [deselectedTransactions, setDeselectedTransactions] = useState<
    OrionTransaction[]
  >([]);
  const [getReportPdf, { isLoading: isPdfLoading }] = useGetReportPdfMutation();
  const [getNewReport, { isLoading: isCalculating }] =
    useGetNewReportMutation();
  const { settingsInfo } = useReportSettings();

  // MultiValuesCard hooks - Keep your shares
  const [startingShares, setStartingShares] = useStockReportValue(
    "starting_shares_stock",
    30000,
  );
  const [currentShares, setCurrentShares] = useStockReportValue(
    "current_shares_stock",
    30000,
  );
  const [stockPriceStart, setStockPriceStart] = useStockReportValue(
    "stock_price_start_stock",
    300,
  );
  const [stockPriceNow, setStockPriceNow] = useStockReportValue(
    "stock_price_now_stock",
    360,
  );
  const [stockOnlyReturn, setStockOnlyReturn] = useStockReportValue(
    "stock_only_return_stock",
    20,
  );
  const [upsideParticipation, setUpsideParticipation] = useStockReportValue(
    "upside_participation_stock",
    92,
  );

  // MultiValuesCard hooks - Generate Income
  const [optionIncomePeriod, setOptionIncomePeriod] = useStockReportValue(
    "option_income_period_stock",
    65000,
  );
  const [cumulativeIncome, setCumulativeIncome] = useStockReportValue(
    "cumulative_income_stock",
    210000,
  );
  const [avgAnnualizedYield, setAvgAnnualizedYield] = useStockReportValue(
    "avg_annualized_yield_stock",
    7,
  );
  const [buybackForfeiture, setBuybackForfeiture] = useStockReportValue(
    "buyback_forfeiture_stock",
    15000,
  );
  const [netOptionIncome, setNetOptionIncome] = useStockReportValue(
    "net_option_income_stock",
    195000,
  );

  // MultiValuesCard hooks - Harvest Tax Benefits
  const [netRealizedLosses, setNetRealizedLosses] = useStockReportValue(
    "net_realized_losses_stock",
    180000,
  );
  const [lossesUsed, setLossesUsed] = useStockReportValue(
    "losses_used_stock",
    60000,
  );
  const [poolRemaining, setPoolRemaining] = useStockReportValue(
    "pool_remaining_stock",
    120000,
  );
  const [sharesSold, setSharesSold] = useStockReportValue(
    "shares_sold_stock",
    4000,
  );
  const [sellableToday, setSellableToday] = useStockReportValue(
    "sellable_today_stock",
    6000,
  );

  // Update keepValues with hooks
  const keepValuesWithHooks: ReportValueCardProps[] = [
    {
      label: "Starting Shares",
      value: startingShares,
      type: SingleValueCardType.NUMBER,
      onValueChange: setStartingShares,
    },
    {
      label: "Current Shares",
      value: currentShares,
      type: SingleValueCardType.NUMBER,
      onValueChange: setCurrentShares,
    },
    {
      label: "Stock Price (Start)",
      value: stockPriceStart,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setStockPriceStart,
    },
    {
      label: "Stock Price (Now)",
      value: stockPriceNow,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setStockPriceNow,
    },
    {
      label: "Stock-Only Return",
      value: stockOnlyReturn,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setStockOnlyReturn,
    },
    {
      label: "Upside Participation",
      value: upsideParticipation,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setUpsideParticipation,
    },
  ];

  // Update generateIncomeValues with hooks
  const generateIncomeValuesWithHooks: ReportValueCardProps[] = [
    {
      label: "Option Income (Period)",
      value: optionIncomePeriod,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setOptionIncomePeriod,
    },
    {
      label: "Cumulative Income",
      value: cumulativeIncome,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setCumulativeIncome,
    },
    {
      label: "Avg. Annualized Yield",
      value: avgAnnualizedYield,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setAvgAnnualizedYield,
    },
    {
      label: "Buyback Forfeiture",
      value: buybackForfeiture,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setBuybackForfeiture,
    },
    {
      label: "Net Option Income",
      value: netOptionIncome,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setNetOptionIncome,
    },
  ];

  // Update harvestTaxBenefitsValues with hooks
  const harvestTaxBenefitsValuesWithHooks: ReportValueCardProps[] = [
    {
      label: "Net Realized Losses",
      value: netRealizedLosses,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setNetRealizedLosses,
    },
    {
      label: "Losses Used",
      value: lossesUsed,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setLossesUsed,
    },
    {
      label: "Pool Remaining",
      value: poolRemaining,
      type: SingleValueCardType.CURRENCY,
      fullWidthRow: true,
      onValueChange: setPoolRemaining,
    },
    {
      label: "Shares Sold",
      value: sharesSold,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesSold,
    },
    {
      label: "Sellable Today",
      value: sellableToday,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSellableToday,
    },
  ];

  // Single source of truth for themed cards: same values drive UI and PDF teamed_info
  const themedCardsConfig = useMemo(
    () => [
      {
        label: "Keep your shares",
        description: "Keep your shares of the stock portfolio",
        values: keepValuesWithHooks,
        addictional_component: {
          type: "bar" as const,
          title: "Overlay vs Stock Only (Illustrative)",
          limit: overlayChartLimit,
          data: [
            {
              label: "Stock Only",
              value: currentValue as number,
              type: "currency" as const,
            },
            {
              label: "Stock + Overlay",
              value: overlayValue as number,
              type: "currency" as const,
            },
          ],
        },
      },
      {
        label: "Generate Income",
        description:
          "Covered calls seek to add a steady income stream, with the trade-off that some upside may be capped.",
        values: generateIncomeValuesWithHooks,
        addictional_component: null,
      },
      {
        label: "Harvest Tax Benefits",
        description:
          "Tax-loss harvesting seeks to create a pool of capital losses to help offset gains. Figures are estimates.",
        values: harvestTaxBenefitsValuesWithHooks,
        addictional_component: null,
      },
    ],
    [
      keepValuesWithHooks,
      generateIncomeValuesWithHooks,
      harvestTaxBenefitsValuesWithHooks,
      currentValue,
      overlayValue,
      overlayChartLimit,
    ],
  );

  const cardTypeToPdfType = useCallback(
    (
      type: SingleValueCardType,
      label: string,
    ): "number" | "currency" | "percentage" | "shares" => {
      if (label.toLowerCase().includes("share")) return "shares";
      return type as "number" | "currency" | "percentage";
    },
    [],
  );

  const buildPdfRequest = useCallback((): PDFReportRequest => {
    const table_columns: PDFReportTableColumnRow[] = tableRows.map((row) => ({
      id: row.id,
      Period: row[ReportQuarterTableColumn.Period],
      "Stock Price": row[ReportQuarterTableColumn.StockPrice],
      "Option Income": row[ReportQuarterTableColumn.OptionIncome],
      "Dividend Income": row[ReportQuarterTableColumn.DividenIncome],
      "Cumulative Income": row[ReportQuarterTableColumn.CumulativeIncome],
      Comment: row[ReportQuarterTableColumn.Comment],
    }));

    const client_info: PDFReportClientInfoItem[] = [
      {
        label: "Starting Value",
        value: String(startingValue),
        type: "currency",
        description: "Beginning balance",
      },
      {
        label: "Current Value",
        value: String(currentValue),
        type: "currency",
        description: "Current Value of the stock portfolio",
      },
      {
        label: "Cumulative Call Income",
        value: String(cumulativeCallIncome),
        type: "currency",
        description: "Cumulative Call Income of the stock portfolio",
      },
      {
        label: "Net Realized TLH Losses",
        value: String(netRealizedTLHLosses),
        type: "currency",
        description: "Net Realized TLH Losses of the stock portfolio",
      },
      {
        label: "Overlay vs Stock Alone",
        value: String(overlayVsStockAlone),
        type: "percentage",
        description: "Overlay vs Stock Alone of the stock portfolio",
      },
      {
        label: "Overlay Return (TWR)",
        value: String(overlayReturnTWR),
        type: "percentage",
        description: "Overlay Return (TWR) of the stock portfolio",
      },
    ];

    const toPdfValues = (items: ReportValueCardProps[]): PDFReportValueItem[] =>
      items.map((item) => ({
        label: item.label,
        value: Number(item.value),
        type: cardTypeToPdfType(item.type, item.label),
      }));

    const theme_info: PDFReportThemeInfoItem[] = themedCardsConfig.map(
      (card) => ({
        label: card.label,
        description: card.description,
        values: toPdfValues(card.values),
        addictional_component: card.addictional_component,
      }),
    );

    return {
      type: "stock",
      settings_info: settingsInfo,
      table_columns,
      client_info,
      theme_info,
    };
  }, [
    tableRows,
    themedCardsConfig,
    settingsInfo,
    startingValue,
    currentValue,
    cumulativeCallIncome,
    netRealizedTLHLosses,
    overlayVsStockAlone,
    overlayReturnTWR,
    cardTypeToPdfType,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    try {
      const response: ReportPdfResponse =
        await getReportPdf(buildPdfRequest()).unwrap();
      if (response.download_url) {
        const a = document.createElement("a");
        a.href = response.download_url;
        a.click();
        a.remove();
        return;
      }
      throw new Error("Failed to download PDF");
    } catch (error) {
      throw new Error("Failed to download PDF");
    }
  }, [getReportPdf, buildPdfRequest]);

  const openTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(true);
  }, []);

  const closeTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(false);
  }, []);

  const handleCalculate = useCallback(async () => {
    const [start_date, end_date] = settingsInfo.period;

    if (
      !settingsInfo.orion_account_id ||
      !settingsInfo.strategy_id ||
      !start_date ||
      !end_date
    ) {
      setCalculationError(
        "Cannot calculate report: missing account, strategy, or date range.",
      );
      return;
    }

    try {
      const response = await getNewReport({
        orion_account_id: settingsInfo.orion_account_id,
        strategy_id: settingsInfo.strategy_id,
        start_date,
        end_date,
        report_type: "stock",
        exclude_transactions:
          deselectedTransactions.length > 0
            ? deselectedTransactions.map((transaction) => transaction.id)
            : undefined,
      }).unwrap();

      const cards = response.data.single_info_cards;
      setStartingValue(cards.card_1);
      setCurrentValue(cards.card_2);
      setCumulativeCallIncome(cards.card_3);
      // TODO: Add net realized TLH losses and overlay vs stock alone after the discussion with clients
      // setNetRealizedTLHLosses(cards.card_4);
      // setOverlayVsStockAlone(cards.card_5);
      setOverlayReturnTWR(cards.card_6);

      // Map backend table_data into quarter table rows, preserving existing comments if present.
      const backendRows = response.data
        .graph_or_table_data as any[]; // STOCK case returns table_data
      const nextTableRows: QuarterRow[] = backendRows.map(
        (row, index): QuarterRow => ({
          id: index + 1,
          [ReportQuarterTableColumn.Period]: row.header_quarter,
          [ReportQuarterTableColumn.StockPrice]: row.stock_price,
          [ReportQuarterTableColumn.OptionIncome]: row.option_income,
          [ReportQuarterTableColumn.DividenIncome]: row.dividend_income,
          [ReportQuarterTableColumn.CumulativeIncome]: row.cumulative_income,
          // Keep existing comment (or default) so user-edited text is preserved
          [ReportQuarterTableColumn.Comment]:
            tableRows[index]?.[ReportQuarterTableColumn.Comment] ??
            defaultQuarterRows[index]?.[ReportQuarterTableColumn.Comment] ??
            "",
        }),
      );
      setTableRows(nextTableRows);
    } catch (error) {
      setCalculationError(
        "Failed to calculate stock report. Please try again.",
      );
    }
  }, [
    settingsInfo.period,
    settingsInfo.orion_account_id,
    settingsInfo.strategy_id,
    deselectedTransactions,
    getNewReport,
    setStartingValue,
    setCurrentValue,
    setCumulativeCallIncome,
    setOverlayReturnTWR,
    setCalculationError,
  ]);

  return (
    <Card>
      <CardContent>
        <RequestNotification
          open={Boolean(calculationError)}
          title="Calculation failed"
          message={calculationError}
          onClose={() => setCalculationError("")}
        />
        <SettingsReport
          type={PerformanceReportTypeEnum.STOCK}
          header="Optic Single Stock with Tax Efficiency Engine"
          subtitle="Covered Call & Tax-Loss Harvesting Overlay"
        />
        <Grid container justifyContent="center" sx={{ mt: 1 }}>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPdf}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Generating…" : "Download PDF"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCalculate}
              disabled={isPdfLoading || isCalculating}
            >
              {isCalculating ? "Calculating…" : `Calculate`}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={openTransactionModal}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Updating…" : "Transactions"}
            </Button>
          </Grid>
        </Grid>
        <ReportQuarterTable rows={tableRows} onRowsChange={setTableRows} />
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={startingValue as number}
              label="Starting Value"
              description="Beginning balance"
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setStartingValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={currentValue as number}
              label="Current Value"
              description="Current Value of the stock portfolio"
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setCurrentValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={cumulativeCallIncome as number}
              label="Cumulative Call Income"
              description="Cumulative Call Income of the stock portfolio"
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setCumulativeCallIncome}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={netRealizedTLHLosses as number}
              label="Net Realized TLH Losses"
              description="Net Realized TLH Losses of the stock portfolio"
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.ERROR}
              onValueChange={setNetRealizedTLHLosses}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={overlayVsStockAlone as number}
              label="Overlay vs. Stock Alone*"
              description="Overlay vs. Stock Alone* of the stock portfolio"
              type={SingleValueCardType.PERCENTAGE}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setOverlayVsStockAlone}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={overlayReturnTWR as number}
              label="Overlay Return (TWR)"
              description="Overlay Return (TWR) of the stock portfolio"
              type={SingleValueCardType.PERCENTAGE}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setOverlayReturnTWR}
            />
          </Grid>
        </Grid>
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          {themedCardsConfig.map((card, index) => (
            <Grid item lg={6} xs={12} md={6} key={card.label}>
              <MultiValuesCard
                title={card.label}
                values={card.values}
                description={card.description}
                additionalComponents={
                  index === 0 ? (
                    <OverlayVsStockChart
                      stockOnlyValue={currentValue as number}
                      stockOverlayValue={overlayValue}
                      limit={overlayChartLimit}
                      onLimitChange={setOverlayChartLimit}
                    />
                  ) : null
                }
              />
            </Grid>
          ))}
        </Grid>
        <TransactionModal
          open={isTransactionModalOpen}
          onClose={closeTransactionModal}
          settingsInfo={settingsInfo}
          reportType={PerformanceReportTypeEnum.STOCK}
          onDeselectedTransactionsChange={setDeselectedTransactions}
        />
      </CardContent>
    </Card>
  );
}

export const StockReport = () => (
  <ReportSettingsProvider>
    <StockReportContent />
  </ReportSettingsProvider>
);

export default StockReport;
